import { useEffect, useState } from "react";
import { ArrowLeft, CheckCircle2, Clock3, PackageCheck, XCircle } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import { useAuth } from "../components/auth/AuthGate";
import {
  acceptTailorQuoteRequest,
  listenToTailorQuoteRequest,
  rejectTailorQuoteRequest,
  tailorQuoteStatusLabel,
} from "../services/tailorQuoteService";

export default function TailorQuoteDetailsPage() {
  const { requestId } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [request, setRequest] = useState(null);
  const [error, setError] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!user || !requestId) return undefined;
    return listenToTailorQuoteRequest(
      requestId,
      (value) => {
        if (!value || value.customerId !== user.uid) {
          setError("Quotation request not found.");
          setRequest(null);
          return;
        }
        setRequest(value);
        setError("");
      },
      () => setError("Unable to load quotation request."),
    );
  }, [user, requestId]);

  const acceptQuote = async () => {
    if (!accepted || saving) return;
    setSaving(true);
    setError("");
    try {
      const orderId = await acceptTailorQuoteRequest(request);
      navigate(`/account/orders/${orderId}`, { replace: true });
    } catch (reason) {
      setError(reason?.message || "Unable to accept this quotation.");
      setSaving(false);
    }
  };

  const rejectQuote = async () => {
    if (saving || !window.confirm("Reject and close this quotation?")) return;
    setSaving(true);
    setError("");
    try {
      await rejectTailorQuoteRequest(request);
      navigate("/account/quotations", { replace: true });
    } catch (reason) {
      setError(reason?.message || "Unable to reject this quotation.");
      setSaving(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-[#FFF8F2]" />;

  const amount = Number(request?.finalQuoteAmount || 0);
  const quoteReady = request?.status === "quote_sent_to_customer" && amount > 0;
  const transitionedToOrder = [
    "customer_accepted",
    "partial_paid",
    "awaiting_final_payment",
    "paid",
    "full_payment_done_at_home",
  ].includes(request?.status);

  return (
    <div className="min-h-screen bg-[#FFF8F2]">
      <Navbar />
      <main className="pt-28 pb-20">
        <section className="max-w-[900px] mx-auto px-6">
          <button onClick={() => navigate("/account/quotations")} className="inline-flex gap-2 items-center text-[#F46524] font-semibold">
            <ArrowLeft size={16} /> My quotations
          </button>

          {error ? <div className="card-soft p-6 mt-8 text-red-600">{error}</div> : null}
          {!request && !error ? <div className="card-soft p-8 mt-8">Loading quotation...</div> : null}

          {request ? (
            <>
              <div className="mt-8">
                <div className="font-label text-[#F46524]">Tailor quotation</div>
                <h1 className="font-display text-4xl md:text-5xl mt-3">{request.garmentName || "Custom design"}</h1>
                <p className="text-[#5b5a56] mt-2 break-all">Request ID: {request.id}</p>
              </div>

              <div className="mt-6 border border-[#F4C9B4] bg-[#FFF3E8] p-5 text-[#5E514A]">
                <p className="font-semibold">Your quotation is saved.</p>
                <p className="text-sm mt-2 leading-relaxed">
                  You can safely close this page and return from Orders, then Quotations. Continue here after the tailor calls and sends the final price.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6 mt-8">
                <div className="bg-[#FFF3E8] min-h-64">
                  {request.designImageUrl || request.designImageDataUrl ? (
                    <img src={request.designImageUrl || request.designImageDataUrl} alt="Design reference" className="w-full aspect-[4/5] object-contain" />
                  ) : (
                    <span className="min-h-64 flex items-center justify-center text-[#F46524]"><PackageCheck size={48} /></span>
                  )}
                </div>

                <div className="space-y-5">
                  <div className="card-soft p-6">
                    <div className="flex gap-3 items-start">
                      {request.status === "awaiting_tailor_quote" ? <Clock3 className="text-[#F46524] shrink-0" /> : <CheckCircle2 className="text-green-700 shrink-0" />}
                      <div>
                        <div className="font-semibold">{tailorQuoteStatusLabel(request.status)}</div>
                        <p className="text-sm text-[#5b5a56] mt-1">
                          {quoteReady ? "The final price is ready for your approval." : "The tailor team will update this request after reviewing the details."}
                        </p>
                      </div>
                    </div>
                  </div>

                  {amount > 0 ? (
                    <div className="card-soft p-6">
                      <div className="text-sm text-[#5b5a56]">Final quotation</div>
                      <div className="font-display text-4xl mt-2">₹{amount.toLocaleString("en-IN")}</div>
                    </div>
                  ) : null}

                  {request.callbackPreferenceLabel ? (
                    <div className="card-soft p-6 text-sm text-[#5b5a56] space-y-2">
                      <p><strong className="text-[#2B2B2B]">Callback:</strong> {request.callbackPreferenceLabel}</p>
                      <p><strong className="text-[#2B2B2B]">Language:</strong> {request.preferredLanguage || "Not specified"}</p>
                    </div>
                  ) : null}

                  {request.customerNotes ? (
                    <div className="card-soft p-6">
                      <h2 className="font-display text-2xl">Your instructions</h2>
                      <p className="text-[#5b5a56] mt-3 whitespace-pre-line">{request.customerNotes}</p>
                    </div>
                  ) : null}

                  {request.tailorNotes ? (
                    <div className="card-soft p-6">
                      <h2 className="font-display text-2xl">Tailor notes</h2>
                      <p className="text-[#5b5a56] mt-3 whitespace-pre-line">{request.tailorNotes}</p>
                    </div>
                  ) : null}

                  {quoteReady ? (
                    <div className="card-soft p-6">
                      <label className="flex items-start gap-3 text-sm">
                        <input type="checkbox" checked={accepted} onChange={(event) => setAccepted(event.target.checked)} className="mt-1 accent-[#F46524]" />
                        <span>I spoke with the tailor, understand the requirements, and accept the final quotation.</span>
                      </label>
                      <div className="grid sm:grid-cols-2 gap-3 mt-5">
                        <button type="button" onClick={rejectQuote} disabled={saving} className="btn-secondary justify-center disabled:opacity-50">
                          <XCircle size={18} /> Reject quotation
                        </button>
                        <button type="button" onClick={acceptQuote} disabled={!accepted || saving} className="btn-primary justify-center disabled:opacity-50">
                          <CheckCircle2 size={18} /> {saving ? "Saving..." : "Accept and continue"}
                        </button>
                      </div>
                    </div>
                  ) : null}

                  {transitionedToOrder ? (
                    <button type="button" onClick={() => navigate(`/account/orders/${request.id}`)} className="btn-primary w-full justify-center">
                      View order
                    </button>
                  ) : null}
                </div>
              </div>
            </>
          ) : null}
        </section>
      </main>
      <Footer />
    </div>
  );
}
